import { Checkbox } from "expo-checkbox";
import React, { useEffect, useState, useRef, PropsWithChildren } from "react";
import { StyleSheet, Platform } from "react-native";
import { TableRowProps } from "@/components/Table/props";
import { useTheme } from "@/hooks/useTheme";
import Animated, {
  useAnimatedReaction,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { Button } from "@/components/ui/Button";
import useContextMenu from "@/hooks/useContextMenu";
import { TableStart } from "./TableStart";
import { TableEnd } from "./TableEnd";

const Anchor = ({ children }: PropsWithChildren) => children;
Anchor.displayName = "TableRow.Anchor";
const Cells = ({ children }: PropsWithChildren) => children;
Cells.displayName = "TableRow.Cells";

export function TableRow({
  style,
  selectable,
  rowIndex,
  onSelect,
  onUnmount,
  children,
  selected,
  scrollX,
  onAction,
  rowNavIcon,
  loading,
  uid,
}: TableRowProps) {
  const { colors } = useTheme();
  const [localSelected, setLocalSelected] = useState(selected);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useAnimatedRef<Animated.ScrollView>();
  const buttonRef = useRef<any>(null);
  const contextMenu = useContextMenu();
  const immediateSelectRef = useRef(false);
  const scrollAnchor = useSharedValue<number | null>(null);
  const maxScroll = useSharedValue(100);
  const closeDelta = 100;
  const all = React.Children.toArray(children);
  const anchor = all.find((c) => React.isValidElement(c) && c.type === TableRow.Anchor);
  const cells = all.find((c) => React.isValidElement(c) && c.type === TableRow.Cells);

  useEffect(() => {
    return () => {
      onUnmount?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalSelected(selected);
  }, [selected]);

  useEffect(() => {
    // Skip delayed callback if we already handled it immediately (web with shift detection)
    if (immediateSelectRef.current) {
      immediateSelectRef.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      if (localSelected !== selected && onSelect) {
        onSelect();
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [localSelected, selected, onSelect]);

  const handleToggle = (shiftKey?: boolean) => {
    if (!loading) {
      setLocalSelected((prev) => !prev);
      if (shiftKey !== undefined && onSelect) {
        immediateSelectRef.current = true; // Mark that we handled it immediately
        // Pass shift key to parent immediately for web
        onSelect(shiftKey);
      }
    }
  };

  const scrollHandler = useAnimatedScrollHandler((event) => {
    const currentX = event.contentOffset.x;
    if (contextMenu.visible) {
      if (scrollAnchor.value === null) scrollAnchor.value = currentX;
      if (scrollAnchor.value === null) {
        scrollAnchor.value = currentX;
      }
      const diff = Math.abs(currentX - scrollAnchor.value);
      if (diff > closeDelta) {
        // runOnJS(contextMenu.setVisible)(false);
        contextMenu.setVisible(false);
        scrollAnchor.value = null;
      }
    }
    scrollX.set(event.contentOffset.x);
    maxScroll.set(event.contentSize.width - event.layoutMeasurement.width);
  });

  useAnimatedReaction(
    () => scrollX.get(),
    (x) => {
      ref.current?.scrollTo({ x, animated: false });
    },
    [scrollX],
  );

  return (
    <Animated.View
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      style={[styles.container, style, isHovered && { backgroundColor: colors.cardHover }]}
    >
      <TableStart selectable={selectable} borderColor={colors.border}>
        {selectable &&
          (Platform.OS === "web" ? (
            <input
              tabIndex={-1}
              type="checkbox"
              onChange={(e) => handleToggle((e.nativeEvent as MouseEvent).shiftKey)}
              checked={localSelected}
              id={`check_${uid}`}
            ></input>
          ) : (
            <Checkbox
              id={`check_${uid}`}
              style={styles.checkbox}
              value={localSelected}
              onValueChange={() => handleToggle()}
            />
          ))}
        <Button
          ref={buttonRef}
          tabIndex={-1}
          style={styles.button}
          icon={rowNavIcon}
          iconSize={16}
          onPress={() => onAction?.(buttonRef)}
        />
      </TableStart>
      <Animated.View
        style={[
          styles.anchor,
          { backgroundColor: colors.card, borderColor: colors.border },
          isHovered && { backgroundColor: colors.cardHover },
        ]}
      >
        {anchor}
      </Animated.View>
      <Animated.ScrollView
        ref={ref}
        horizontal={true}
        scrollEnabled={!loading}
        showsHorizontalScrollIndicator={false}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        style={[styles.row]}
      >
        {cells}
      </Animated.ScrollView>
      <TableEnd borderColor={colors.border} />
    </Animated.View>
  );
}

TableRow.Anchor = Anchor;
TableRow.Cells = Cells;

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 1,
    elevation: 2,
    flexDirection: "row",
    height: 38,
    alignSelf: "flex-start",
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "auto",
    maxWidth: "100%",
    width: "100%",
  },
  button: {
    height: 18,
    width: 18,
    backgroundColor: "transparent",
    borderWidth: 0,
  },
  anchor: {
    minHeight: 32,
    flexDirection: "row",
  },
  row: {
    flex: 1,
    minHeight: 32,
    flexDirection: "row",
  },
  checkbox: {},
  menu: {
    right: 34,
  },
  menuButton: {
    minWidth: 100,
    padding: 2,
    paddingHorizontal: 4,
    justifyContent: "space-between",
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
});
